import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { mergeDeepRight, mergeRight, not, remove } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import CategoriesDialog from "../edit/CategoriesDialog";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const ProjectCategoriesCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const update = mergeRight(project, { categories: data });
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        categories: remove(idx, 1, project.categories),
      });

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title="Categories"
        action={
          <Tooltip title="Add Categories">
            <IconButton onClick={handleAddClick} size="large">
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Categories.
          </Box>
        )}
        {isBlank(project.categories) && not(project.useDefaultCategories) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Categories are set up for this Project.
          </Box>
        )}
        {project.useDefaultCategories && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            This Project uses the default set of Categories
          </Box>
        )}
        {notBlank(project.categories) && not(project.useDefaultCategories) && (
          <List dense style={{ maxHeight: 400, overflowY: "auto" }}>
            {mapIndexed(
              (cat, idx) => (
                <ListItem key={cat.categoryId}>
                  <ListItemText primary={cat.categoryName} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Remove Project Category"
                      dialogBody="Are you sure you want to remove this project category?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              project.categories,
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <CategoriesDialog
          project={project}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default ProjectCategoriesCard;
